import { Box, Button, Drawer, Group, Text } from "@mantine/core"
import { useDisclosure, useMediaQuery } from "@mantine/hooks"
import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import type { PriceBreakdown } from "../../../apis/pricing"
import type { IAuth } from "../../../store/Auth"
import type { CartItem } from "../../../store/Cart"
import { message } from "../../../utils/message"
import { usePermission } from "../../permission"
import { ListState, PermissionButton, ReasonTooltip } from "../../ui"
import { SaveListModal } from "../saved-lists/SaveListModal"
import {
  checkoutErrorText,
  extraDiscountOf,
  groupVatAfterDiscount,
  inactiveNetworkReason,
  isConsignmentGroup,
  promoPricedCount,
  promotionSummaryText,
  resolveTermId,
  submitCart,
  useCartPreviews,
  useSupplierNetworks,
} from "./cart-model"
import { CartDrawerItems } from "./CartDrawerItems"
import classes from "./CartDrawer.module.css"
import { CartTotals } from "./CartTotals"
import type { SupplierCartGroup } from "./types"
import { useFinishCheckout } from "./use-finish-checkout"

type Props = {
  opened: boolean
  onClose: () => void
  cart: CartItem[]
  cartBySupplier: Record<string, SupplierCartGroup>
  totalItems: number
  totalAmount: number
  /** Cart lines priced at their cart quantity, keyed `${supplierId}:${variantId}` (catalogue page). */
  breakdowns: Record<string, PriceBreakdown>
  onUpdateQuantity: (productId: string, supplierBusinessId: string, qty: number) => void
  onRemoveFromCart: (productId: string, supplierBusinessId: string) => void
}

/**
 * Cart drawer (canvas OrdB_Cart, B23): 720 wide, opened under the app header,
 * lines grouped by supplier, totals, then «Ноорог хадгалах» / «Захиалга
 * үргэлжлүүлэх». Delivery and payment terms are chosen on the checkout page.
 */
export function CartDrawer({
  opened,
  onClose,
  cart,
  cartBySupplier,
  totalItems,
  totalAmount,
  breakdowns,
  onUpdateQuantity,
  onRemoveFromCart,
}: Props) {
  const { t } = useTranslation(["order", "common"])
  const navigate = useNavigate()
  const isMobile = useMediaQuery("(max-width: 62em)")
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const finish = useFinishCheckout()
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")

  const supplierIds = Object.keys(cartBySupplier)
  const { networks, isLoading: networksLoading } = useSupplierNetworks(opened ? supplierIds : [])
  const [saving, setSaving] = React.useState(false)
  const [saveListOpened, { open: openSaveList, close: closeSaveList }] = useDisclosure(false)

  // D3: gift lines and the bundle / threshold discount order create will apply.
  const previews = useCartPreviews(cartBySupplier, opened && (ORD_SAVE.hasAccess || ORD_SEND.hasAccess))
  const promoDiscount = supplierIds.reduce((sum, id) => sum + extraDiscountOf(previews, id, cartBySupplier[id].totalAmount), 0)
  const promoPriced = promoPricedCount(cart, breakdowns)
  // The order discount is spread over the lines, so the VAT shrinks with it.
  const vat = supplierIds.reduce(
    (sum, id) => sum + groupVatAfterDiscount(cartBySupplier[id], extraDiscountOf(previews, id, cartBySupplier[id].totalAmount)),
    0,
  )
  const promotionText = promotionSummaryText(t, promoPriced, previews)

  // Why «Ноорог хадгалах» cannot run from here. A draft from the drawer uses each
  // supplier's network defaults (receiver branch and staff, default term); the
  // consignment endpoint needs a delivery date, so those carts save from checkout.
  const draftReason = (() => {
    if (cart.length === 0) return t("order:cart.reasonEmpty")
    if (supplierIds.some((id) => isConsignmentGroup(cartBySupplier[id]))) return t("order:cart.reasonDraftConsignment")
    if (networksLoading) return t("order:cart.reasonDraftLoading")
    const inactive = inactiveNetworkReason(t, supplierIds, networks, cartBySupplier)
    if (inactive) return inactive
    const missing = supplierIds.find((id) => {
      const n = networks[id]
      return !n || !n.receiverBranchId || !n.receiverStaff?.id || !n.supplierStaff?.id || !n.buyerStaff?.id || !(resolveTermId(n) || n.paymentTermId)
    })
    if (missing) return t("order:cart.reasonDraftSetup", { name: cartBySupplier[missing]?.supplierName })
    return null
  })()

  // B40: lines of an inactive network have to leave the cart before checkout.
  const continueReason =
    cart.length === 0
      ? t("order:cart.reasonEmpty")
      : !ORD_SEND.hasAccess && !ORD_SAVE.hasAccess
        ? t("order:cart.reasonNoPerm")
        : networksLoading
          ? null
          : inactiveNetworkReason(t, supplierIds, networks, cartBySupplier)

  const handleDraft = async () => {
    if (draftReason || saving) return
    setSaving(true)
    try {
      const results = await submitCart({
        groups: cartBySupplier,
        networks,
        suppliers: supplierIds.map((id) => {
          const n = networks[id]
          return {
            supplierId: id,
            deliveryType: "DEFAULT_DATE",
            deliveryDate: "",
            paymentTermId: resolveTermId(n) || n?.paymentTermId,
            receiverBranchId: n?.receiverBranchId,
            receiverStaffId: n?.receiverStaff?.id,
          }
        }),
        receiverBranchId: "",
        receiverStaffId: "",
        senderNote: "",
        senderAdditionalNote: "",
        send: false,
        user,
      })
      results
        .filter((r) => !r.orderId)
        .forEach((r) => message.error(`${r.supplierName}: ${checkoutErrorText(t, r.error, cart)}`))
      if (finish(false, results, cartBySupplier)) onClose()
    } finally {
      setSaving(false)
    }
  }

  const handleContinue = () => {
    if (continueReason) return
    onClose()
    navigate("/buyer-sales/checkout")
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : 720}
      padding={0}
      title={
        <Box>
          <Text className={classes.drawerTitle}>{t("order:cart.title")}</Text>
          <Text className={classes.sub}>{t("order:cart.headSub", { types: cart.length, count: totalItems })}</Text>
        </Box>
      }
      closeButtonProps={{ "aria-label": t("order:cart.close"), size: "lg" }}
      overlayProps={{ backgroundOpacity: 0.35 }}
      classNames={{
        overlay: classes.drawerOverlay,
        inner: classes.drawerInner,
        content: classes.drawerContent,
        header: classes.drawerHeader,
        body: classes.drawerBody,
      }}
    >
      <Box className={classes.drawerScroll}>
        {cart.length === 0 ? (
          <ListState
            state="empty"
            title={t("order:cart.emptyTitle")}
            description={t("order:cart.emptyText")}
            action={{ label: t("order:cart.emptyAction"), onClick: onClose }}
          />
        ) : (
          <>
            <CartDrawerItems
              cartBySupplier={cartBySupplier}
              networks={networks}
              breakdowns={breakdowns}
              previews={previews}
              onUpdateQuantity={onUpdateQuantity}
              onRemoveFromCart={onRemoveFromCart}
            />
            <Box className={`${classes.card} ${classes.cardPad}`}>
              <CartTotals
                types={cart.length}
                pieces={totalItems}
                subtotal={totalAmount}
                promotionText={promotionText}
                promoDiscount={promoDiscount}
                vat={vat}
                total={totalAmount - promoDiscount}
                totalLabel={t("order:cart.total")}
              />
              {supplierIds.length > 1 && (
                <Text className={classes.sub} mt="sm">
                  {t("order:cart.separateOrders", { count: supplierIds.length })}
                </Text>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box className={classes.drawerFoot}>
        <Group gap="sm">
          <PermissionButton
            permission="ORD_SAVE"
            variant="default"
            size="md"
            loading={saving}
            disabledReason={draftReason}
            onClick={handleDraft}
          >
            {t("order:cart.saveDraft")}
          </PermissionButton>
          {/* B25: the cart's lines of one supplier become a saved list. */}
          <Button variant="subtle" color="gray" size="md" disabled={cart.length === 0} onClick={openSaveList}>
            {t("bulkOrder:saved.saveAsList")}
          </Button>
        </Group>
        <ReasonTooltip reason={continueReason}>
          <Button size="md" onClick={handleContinue} title={continueReason ? undefined : t("order:cart.continueHint")}>
            {t("order:cart.continue")}
          </Button>
        </ReasonTooltip>
      </Box>
      <SaveListModal
        opened={saveListOpened}
        onClose={closeSaveList}
        groups={supplierIds.map((id) => ({
          supplierBusinessId: id,
          supplierName: cartBySupplier[id]?.supplierName || "",
          lines: cartBySupplier[id].items.map((i) => ({ variantId: i.variantId, parentVariantId: i.variantParentId, quantity: i.quantity })),
        }))}
      />
    </Drawer>
  )
}
